import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { Button, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import TopBar from '../../../../Layout/components/TopBar/TopBar';
import { tabs } from '../../api/models/ProjectDetailTabs';
import { ProjectUserRole } from '../../api/models/ProjectUserPermission';
import LeaveProjectPopup from '../../components/LeaveProjectPopup/LeaveProjectPopup';
import ProjectTabPanels from '../../components/ProjectTabPanels/ProjectTabPanels';
import ProjectTabSkeleton, {
    ProjectTabSkeletonVariant,
} from '../../components/ProjectTabSkeleton/ProjectTabSkeleton';
import RemoveProjectPopup from '../../components/RemoveProjectPopup/RemoveProjectPopup';
import useProjectDetails from '../../hooks/useProjectDetails';
import styles from './ProjectDetails.module.scss';

const ProjectDetails = () => {
    const { id, tab } = useParams();
    const [isRemoveProjectPopupOpen, setIsRemoveProjectPopupOpen] = useState(false);
    const [isLeaveProjectPopupOpen, setIsLeaveProjectPopupOpen] = useState(false);
    const { isLoading, projectDetails, permissions, handleRemove, handleLeave } =
        useProjectDetails(id!);
    const isArchived = projectDetails?.isArchived ?? false;

    const userCanEdit = useMemo(
        () =>
            permissions &&
            (permissions.isAdmin ||
                permissions.projectRoles.some(
                    (pr) =>
                        pr === ProjectUserRole.ProductOwner || pr === ProjectUserRole.ScrumMaster,
                )) &&
            !isArchived,
        [permissions, isArchived],
    );

    const userCanLeave = useMemo(
        () =>
            permissions &&
            permissions.projectRoles.length > 0 &&
            !permissions.projectRoles.includes(ProjectUserRole.ProductOwner) &&
            !isArchived,
        [permissions, isArchived],
    );

    const canManageAzureResources = useMemo(
        () =>
            permissions &&
            !isArchived &&
            (permissions.isAdmin ||
                (permissions.accessToTools.includes('VSTS:ARTIFACT') &&
                    permissions.projectRoles.some((pr) => pr !== ProjectUserRole.StakeHolder))),
        [permissions, isArchived],
    );

    const canManageGithubRepositories = useMemo(
        () =>
            permissions &&
            !isArchived &&
            (permissions.isAdmin ||
                permissions.projectRoles.some((pr) => pr !== ProjectUserRole.StakeHolder)),
        [permissions, isArchived],
    );

    const skeletonVariant: ProjectTabSkeletonVariant =
        tab === 'tools'
            ? 'tools'
            : tab === 'users'
            ? 'users'
            : tab === 'costs'
            ? 'costs'
            : tab === 'history'
            ? 'history'
            : 'details';

    return (
        <div
            data-testid='projectdetails'
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            <TopBar>
                <div className={styles.details__topBarContainer}>
                    {projectDetails && (
                        <div>
                            <Typography variant='h4'>
                                {projectDetails.projectName}
                                {permissions?.isAdmin && projectDetails.packageName && (
                                    <span
                                        style={{
                                            fontSize: '0.8em',
                                            color: '#666',
                                            marginLeft: '8px',
                                        }}
                                    >
                                        ({projectDetails.packageName})
                                    </span>
                                )}
                            </Typography>
                        </div>
                    )}

                    <div className={styles.details__actions}>
                        
                        {userCanLeave && (
                            <>
                                <Button
                                    variant='text'
                                    onClick={() => setIsLeaveProjectPopupOpen(true)}
                                    startIcon={<LogoutIcon />}
                                >
                                    Leave Project
                                </Button>
                                <LeaveProjectPopup
                                    open={isLeaveProjectPopupOpen}
                                    handleClose={() => setIsLeaveProjectPopupOpen(false)}
                                    handleSubmit={async () => {
                                        setIsLeaveProjectPopupOpen(false);
                                        await handleLeave();
                                    }}
                                />
                            </>
                        )}


                        {userCanEdit && (
                            <>
                                <Button
                                    variant='text'
                                    color='error'
                                    onClick={() => setIsRemoveProjectPopupOpen(true)}
                                    startIcon={<DeleteOutlinedIcon />}
                                >
                                    Delete Project
                                </Button>
                                <RemoveProjectPopup
                                    projectName={projectDetails?.projectName || ''}
                                    open={isRemoveProjectPopupOpen}
                                    handleClose={() => setIsRemoveProjectPopupOpen(false)}
                                    handleSubmit={async () => {
                                        setIsRemoveProjectPopupOpen(false);
                                        await handleRemove();
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </TopBar>

            <div style={{ position: 'relative', flex: 1 }}>
                {isLoading || !projectDetails ? (
                    <ProjectTabSkeleton variant={skeletonVariant} />
                ) : (
                    <ProjectTabPanels
                        project={projectDetails}
                        userCanEdit={userCanEdit || false}
                        canManageAzureResources={canManageAzureResources || false}
                        canManageGithubRepositories={canManageGithubRepositories || false}
                        routeTabIndex={tabs.indexOf(tab || 'details')}
                    />
                )}
            </div>
        </div>
    );
};

export default ProjectDetails;
